import { LocateFixed } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { ToastAlert } from "src/components/ui/toaster";
import { useToast } from "src/components/ui/use-toast";
import { set_appointment_form } from "src/config/redux/slicer/health-care/appointmentSlicer";
import { USER_LOCAL_STORAGE_LOCATION, getAddressLabel, DEFAULT_COORDS } from "src/config/services/fellas/google.api";
import { calculateDistance, getCurrentUserLocation, getDragLocation } from "src/lib/utils";
import LeafletMapView from "src/components/map/LeafletMapView";
import LocationSearchInput, { PlaceSearchResult } from "src/components/map/LocationSearchInput";

export { FellasPin } from "src/components/map/fellasIcon";

interface DialogLocationUserType {
  is_appointment_form?: boolean;
}

function DialogLocationUser({ is_appointment_form }: DialogLocationUserType) {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const [{ city, province, address, coords, full_address }, setUserLocation] = useState(USER_LOCAL_STORAGE_LOCATION);
  const { appointment_form } = useSelector((state: any) => state.Appointment);
  const { selected_health_care } = useSelector((state: any) => state.SearchQuery);

  const activeCoords = coords && typeof coords.lat === 'number' && typeof coords.lng === 'number' ? coords : DEFAULT_COORDS;

  const updateUserLocationForm = async () => {
    localStorage.setItem('user_location', JSON.stringify({ city, province, address, full_address, coords: activeCoords }));
    ToastAlert({}, 'Lokasi Berhasil di update');
    !is_appointment_form && window.location.reload();
  };

  const onDragUserLocation = async (location: any) => {
    const res = await getDragLocation(location);
    setUserLocation(res);
  };

  useEffect(() => {
    if (!address || !coords) {
      getCurrentUserLocation().then((user_location: any) => {
        if (user_location) {
          setUserLocation(user_location);
        }
      });
    }

    if (is_appointment_form && selected_health_care?.homecare_area?.coords) {
      const homecare_coords = selected_health_care.homecare_area.coords;
      const homecare = appointment_form?.homecare || {};
      const distance = calculateDistance(homecare_coords, activeCoords);
      const new_appointment_form = {
        ...appointment_form,
        homecare: { ...homecare, user_location: { address, coords: activeCoords, full_address, distance } },
      };
      dispatch(set_appointment_form(new_appointment_form));
      const is_homecare_range = distance <= (selected_health_care.homecare_area.radius || 0);
      if (!is_homecare_range) {
        toast({
          variant: 'destructive',
          title: `[Out of Range] Something went wrong!`,
          description: `Titik diluar jangkauan homecare`,
        });
      }
    }
  }, [address]);

  return (
    <DialogContent className="sm:max-w-[425px] lg:min-w-[500px] bg-white">
      <DialogHeader>
        <DialogTitle className="text-center">Tentukan Lokasi Anda</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <div className="h-52 rounded-sm overflow-hidden border border-gray-200">
          <LeafletMapView
            center={activeCoords}
            zoom={15}
            markerPosition={activeCoords}
            draggable={true}
            onDragEnd={onDragUserLocation}
            onMapClick={onDragUserLocation}
          />
        </div>

        <div className="flex gap-2 items-end">
          <div className="w-full">
            <p className="mb-1 text-xs text-gray-600">Cari alamat lokasi anda</p>
            <LocationSearchInput
              placeholder={full_address || "Cari alamat lokasi anda..."}
              currentCoords={activeCoords}
              onPlaceSelected={(place: PlaceSearchResult) => {
                const { city, province, address_components: address, full_address, coords } = place;
                setUserLocation({ city, province, address, full_address, coords });
              }}
            />
          </div>
        </div>

        <div className="flex justify-between gap-2 items-center pt-1">
          <div className="w-3/4 flex gap-2 items-center">
            <LocateFixed
              className="text-primary cursor-pointer hover:animate-pulse flex-shrink-0"
              onClick={() => {
                getCurrentUserLocation().then((user_location: any) => {
                  if (user_location) setUserLocation(user_location);
                });
              }}
            />
            <div className="overflow-hidden">
              <p className="font-medium text-gray-800 text-xs truncate">{city ? `${city}, ${province}` : 'Lokasi Terpilih'}</p>
              <p className="text-xs text-gray-400 truncate">{getAddressLabel(address || [], 'route')}</p>
            </div>
          </div>
          {!is_appointment_form && <Button onClick={updateUserLocationForm}>Terapkan</Button>}
        </div>
      </div>
    </DialogContent>
  );
}

export default DialogLocationUser;
